import React, { useState, ReactNode } from 'react';
import first from 'lodash.first';

import CountryFlag from 'components/base/CountryFlag';
import DoubleMetricCell from 'components/data-visualization/Table/DoubleMetricCell';

import {
  TableRow as StyledTableRow,
  PositionCell,
  CountryCell,
  AlignContent,
} from '../shared/TerritoryTable.style';

import { ICountryData } from 'components/data-containers/ChoroplethMap/types';

type TableRowType = {
  tableHeaders: {
    change: string;
    deprecatedKey: string;
    key: string;
    label: string;
    range: string;
    type: string;
    value: string;
  }[];
  territory: {
    countryId: string;
    countryName: string;
    data?: ICountryData[];
    displayName: string;
    flagImgLarge: string;
    flagImgMedium: string;
    flagImgSmall: string;
    id: string;
    lat: number;
    lon: number;
  };
  sortMetric: {
    label: string;
    value: string;
    key: string;
    type: 'number' | 'trend';
    range: string;
    subCells: string[];
    customerName: string;
  };
  children: ReactNode;
};

/**
 * (Territory) TableRow formats and renders the data of a country in a row.
 *
 * @param  {Object} props.territory the territory for the current row
 * @param  {Object} props.sortMetric metric to sort by
 * @param  {Object} props.children renders `ExpandableTableRow` when passed
 * @param  {Boolean} props.isStatic if true this row is not be affected by sorting
 *                                  and is styled differently
 */
const TableRow: React.FC<TableRowType> = (props) => {
  const { tableHeaders, territory, sortMetric, children } = props;

  const [isExpanded, setIsExpanded] = useState(false);
  const entityData = first(territory?.data);

  // On large screens, filter nothing, otherwise only return
  // the active metric as mobile is single column.
  function toggleExpand(): void {
    if (children) {
      setIsExpanded(!isExpanded);
    }
  }

  return (
    <StyledTableRow
      onClick={toggleExpand}
      withExpandableRows={Boolean(children)}
      isStatic={false}
    >
      {tableHeaders.map((header: any) => {
        if (header?.value === 'countryRank')
          return (
            <PositionCell key={Object.values(header).join('-')}>
              {(entityData as any).countryRank}
            </PositionCell>
          );

        if (header?.value === 'countryName')
          return (
            <CountryCell key={Object.values(header).join('-')}>
              <AlignContent alignLeft>
                {
                  <CountryFlag
                    id={territory.id || territory.countryId}
                    displayName={territory.countryName}
                    flagImgSmall={territory.flagImgSmall}
                    flagImgMedium={territory.flagImgMedium}
                    flagImgLarge={territory.flagImgLarge}
                    small
                  />
                }
                {territory.countryName}
              </AlignContent>
            </CountryCell>
          );

        return (
          <DoubleMetricCell
            key={Object.values(header).join('-')}
            sortBy={sortMetric}
            header={header}
            entityData={entityData}
            paddingRight="2em"
            tableName="streamingTerritories"
          />
        );
      })}
    </StyledTableRow>
  );
};

export default TableRow;
